/**
 * LES DATES, LE COMPLÉMENT PUR DE `fmtDate` (L-1, 06/10/2026).
 *
 * `fmtDate` (lib/meta.ts) est la seule fonction qui ÉCRIT une date lue. Ce
 * module porte ce qui l'entoure, pour qu'aucun écran n'ait à découper une
 * chaîne ISO ni à appeler `toLocaleDateString` lui-même :
 *
 *  · `jourIso` / `isoJour` / `aujourdhuiIso` — le jour AAAA-MM-JJ d'une valeur,
 *    pour comparer, indexer ou remplir un `<input type="date">`. Jamais pour
 *    afficher : l'affichage passe par `fmtDate`.
 *  · `fmtDateHeure` — « jj/mm/aaaa hh:mm », dans le fuseau de la personne.
 *
 * Module PUR (aucun import d'exécution) : `tests/front-dates.test.js` le rejoue.
 */

/** Le jour AAAA-MM-JJ d'une valeur (date ou horodatage ISO). Vide si absente. */
export const jourIso = (v?: unknown): string => String(v ?? '').slice(0, 10);

/** Le jour AAAA-MM-JJ d'un objet `Date`, en UTC (comme `toISOString`). */
export const isoJour = (d: Date): string => d.toISOString().slice(0, 10);

/** Aujourd'hui, AAAA-MM-JJ (UTC). */
export const aujourdhuiIso = (): string => isoJour(new Date());

const deux = (n: number) => String(n).padStart(2, '0');

/**
 * « 06/10/2026 14:12 » — la date ET l'heure, lues dans le fuseau local.
 *
 * ⚠️ Une valeur sans heure (« 2026-10-06 ») ne passe PAS par `Date` : elle y
 * serait lue en UTC puis rendue en local, et le jour pourrait reculer. Elle
 * sort donc en « 06/10/2026 », sans heure inventée.
 * `avecHeure = false` ne garde que le jour local d'un horodatage.
 */
export function fmtDateHeure(v?: Date | string | null, avecHeure = true): string {
  if (!v) return '—';
  if (typeof v === 'string') {
    const seul = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (seul) return `${seul[3]}/${seul[2]}/${seul[1]}`;
  }
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const jour = `${deux(d.getDate())}/${deux(d.getMonth() + 1)}/${d.getFullYear()}`;
  return avecHeure ? `${jour} ${deux(d.getHours())}:${deux(d.getMinutes())}` : jour;
}
